/**
 * Work-journey member eligibility errors (address, local ID / RUT, pending,
 * inactive). Expected business preconditions — not actionable Sentry alerts.
 *
 * Client asserts attach structured `userIds` / `displayNames` (and optionally
 * `documentKind`). Backend FirebaseErrors may only carry a message suffix
 * `(user ${userId})`; that contract is parsed once here.
 */

export type WorkJourneyExpectedMemberIssueKind =
  | 'missing-address'
  | 'missing-jurisdiction-id'
  | 'members-pending'
  | 'members-inactive';

export type WorkJourneyExpectedMemberIssue = {
  kind: WorkJourneyExpectedMemberIssueKind;
  userIds: string[];
  displayNames: string[];
  documentKind?: string;
};

export const ATTENDANCE_ADDRESS_REQUIRED_MESSAGE =
  'User address is required to assign a work journey or record attendance';

const CLIENT_ASSERT_ADDRESS_FALLBACK_MESSAGE =
  'Users without an address cannot be assigned to a work journey';

export const ATTENDANCE_ADDRESS_REQUIRED_CODE = 'attendance-address-required';

export const ATTENDANCE_JURISDICTION_ID_REQUIRED_MESSAGE =
  'A valid local identifier is required to assign this user to a work journey';

export const ATTENDANCE_JURISDICTION_ID_REQUIRED_CODE = 'attendance-jurisdiction-id-required';

export const ORGANIZATION_USER_PENDING_MESSAGE =
  'Pending users cannot be assigned to a work journey';

export const ORGANIZATION_USER_INACTIVE_MESSAGE =
  'Inactive users cannot be assigned to a work journey';

export const MEMBERS_PENDING_CODE = 'members-pending';
export const MEMBERS_INACTIVE_CODE = 'members-inactive';

type ErrorFields = {
  code: string;
  message: string;
  name: string;
  userIds: string[];
  displayNames: string[];
  documentKind: string;
};

const codeEndsWith = (code: string, suffix: string): boolean =>
  code === suffix || code.endsWith(`/${suffix}`);

const asStringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.length > 0)
    : [];

const extractErrorFields = (error: unknown): ErrorFields | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const maybe = error as {
    code?: unknown;
    message?: unknown;
    name?: unknown;
    userIds?: unknown;
    displayNames?: unknown;
    documentKind?: unknown;
  };

  return {
    code: typeof maybe.code === 'string' ? maybe.code : '',
    message: typeof maybe.message === 'string' ? maybe.message : '',
    name: typeof maybe.name === 'string' ? maybe.name : '',
    userIds: asStringList(maybe.userIds),
    displayNames: asStringList(maybe.displayNames).filter(name => name.trim().length > 0),
    documentKind:
      typeof maybe.documentKind === 'string' ? maybe.documentKind.trim() : '',
  };
};

/**
 * Backend message contract only: `… (user ${userId})`.
 * Prefer structured `userIds` from client asserts when present.
 */
export const parseBackendUserIdSuffix = (message: string): string[] => {
  const marker = '(user ';
  const start = message.lastIndexOf(marker);
  if (start < 0) {
    return [];
  }
  const idStart = start + marker.length;
  const end = message.indexOf(')', idStart);
  if (end < 0) {
    return [];
  }
  const userId = message.slice(idStart, end).trim();
  return userId ? [userId] : [];
};

const parseJurisdictionDocumentKind = (message: string): string => {
  const kindMarker = `${ATTENDANCE_JURISDICTION_ID_REQUIRED_MESSAGE}:`;
  const kindStart = message.indexOf(kindMarker);
  if (kindStart < 0) {
    return '';
  }
  const afterKind = message.slice(kindStart + kindMarker.length).trim();
  const kindEnd = afterKind.indexOf(' (user ');
  return (kindEnd >= 0 ? afterKind.slice(0, kindEnd) : afterKind).trim();
};

const resolveUserExtras = (
  fields: ErrorFields,
): Pick<WorkJourneyExpectedMemberIssue, 'userIds' | 'displayNames'> => {
  if (fields.userIds.length > 0) {
    return { userIds: fields.userIds, displayNames: fields.displayNames };
  }
  return {
    userIds: parseBackendUserIdSuffix(fields.message),
    displayNames: fields.displayNames,
  };
};

const classifyMissingAddress = (
  fields: ErrorFields,
): WorkJourneyExpectedMemberIssue | null => {
  const matches =
    fields.code === ATTENDANCE_ADDRESS_REQUIRED_CODE ||
    fields.name === 'AttendanceAddressRequiredError' ||
    fields.message.includes(ATTENDANCE_ADDRESS_REQUIRED_MESSAGE) ||
    fields.message.includes(CLIENT_ASSERT_ADDRESS_FALLBACK_MESSAGE) ||
    (codeEndsWith(fields.code, 'failed-precondition') &&
      /address is required|without an address/i.test(fields.message));

  if (!matches) {
    return null;
  }

  return { kind: 'missing-address', ...resolveUserExtras(fields) };
};

const classifyMissingJurisdictionId = (
  fields: ErrorFields,
): WorkJourneyExpectedMemberIssue | null => {
  const matches =
    fields.code === ATTENDANCE_JURISDICTION_ID_REQUIRED_CODE ||
    fields.name === 'AttendanceJurisdictionIdRequiredError' ||
    fields.message.includes(ATTENDANCE_JURISDICTION_ID_REQUIRED_MESSAGE) ||
    (codeEndsWith(fields.code, 'failed-precondition') &&
      /valid local identifier is required/i.test(fields.message));

  if (!matches) {
    return null;
  }

  const extras = resolveUserExtras(fields);
  const documentKind =
    fields.documentKind || parseJurisdictionDocumentKind(fields.message) || undefined;

  return {
    kind: 'missing-jurisdiction-id',
    ...extras,
    ...(documentKind ? { documentKind } : {}),
  };
};

const classifyMembersPending = (
  fields: ErrorFields,
): WorkJourneyExpectedMemberIssue | null => {
  const matches =
    fields.code === MEMBERS_PENDING_CODE ||
    fields.name === 'MembersPendingError' ||
    fields.message.includes(ORGANIZATION_USER_PENDING_MESSAGE) ||
    (codeEndsWith(fields.code, 'failed-precondition') &&
      /pending users cannot be assigned/i.test(fields.message));

  if (!matches) {
    return null;
  }

  return { kind: 'members-pending', ...resolveUserExtras(fields) };
};

const classifyMembersInactive = (
  fields: ErrorFields,
): WorkJourneyExpectedMemberIssue | null => {
  const matches =
    fields.code === MEMBERS_INACTIVE_CODE ||
    fields.name === 'MembersInactiveError' ||
    fields.message.includes(ORGANIZATION_USER_INACTIVE_MESSAGE) ||
    (codeEndsWith(fields.code, 'failed-precondition') &&
      /inactive users cannot be assigned/i.test(fields.message));

  if (!matches) {
    return null;
  }

  return { kind: 'members-inactive', ...resolveUserExtras(fields) };
};

/**
 * Returns structured issue extras when `error` is an expected member-eligibility
 * rejection; otherwise `null`.
 */
export const classifyWorkJourneyExpectedMemberIssue = (
  error: unknown,
): WorkJourneyExpectedMemberIssue | null => {
  const fields = extractErrorFields(error);
  if (!fields) {
    return null;
  }

  return (
    classifyMissingAddress(fields) ||
    classifyMissingJurisdictionId(fields) ||
    classifyMembersPending(fields) ||
    classifyMembersInactive(fields)
  );
};

export const isExpectedWorkJourneyMemberIssue = (error: unknown): boolean =>
  classifyWorkJourneyExpectedMemberIssue(error) != null;

/** @deprecated Prefer `isExpectedWorkJourneyMemberIssue` / classify by `kind`. */
export const isExpectedAttendanceAddressError = (error: unknown): boolean =>
  classifyWorkJourneyExpectedMemberIssue(error)?.kind === 'missing-address';

/** @deprecated Prefer `isExpectedWorkJourneyMemberIssue` / classify by `kind`. */
export const isExpectedAttendanceJurisdictionIdError = (error: unknown): boolean =>
  classifyWorkJourneyExpectedMemberIssue(error)?.kind === 'missing-jurisdiction-id';

/** @deprecated Prefer `isExpectedWorkJourneyMemberIssue` / classify by `kind`. */
export const isExpectedWorkJourneyMemberStatusError = (error: unknown): boolean => {
  const kind = classifyWorkJourneyExpectedMemberIssue(error)?.kind;
  return kind === 'members-pending' || kind === 'members-inactive';
};

/** @deprecated Prefer classify by `kind === 'members-pending'`. */
export const isExpectedMembersPendingError = (error: unknown): boolean =>
  classifyWorkJourneyExpectedMemberIssue(error)?.kind === 'members-pending';

/** @deprecated Prefer classify by `kind === 'members-inactive'`. */
export const isExpectedMembersInactiveError = (error: unknown): boolean =>
  classifyWorkJourneyExpectedMemberIssue(error)?.kind === 'members-inactive';
